/**
 * Books vs Return — the legacy's Income / GST / Exempt reconciliation tabs.
 *
 * Reads the **TB** sheet of the firm's Main GST Data File. The GST treatment
 * per account — `TAXABLE` / `EXEMPT` / `NA` — is the practitioner's own
 * judgement, written in their workbook. Nothing here decides it; this reports
 * where the books and the return disagree, and names which side each gap is on.
 *
 * The one thing it will not do is total an account nobody classified. An income
 * row with an amount and no remark is a question, so it is listed as one:
 * folding it into taxable would overstate turnover, dropping it would
 * understate, and both are wrong in a way nobody sees.
 */

import { Alert, Button } from '@fiveox/ui'
import { Scale, Upload } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, type PeriodRecord, type TbReconBody, periods } from '../lib/api'
import { useClient } from './ClientLayout'

type Tab = 'income' | 'gst' | 'exempt'

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'income', label: 'Income Recon' },
  { key: 'gst', label: 'GST Recon' },
  { key: 'exempt', label: 'Exempt' },
]

function rupees(paise: unknown, signed = false): string {
  if (typeof paise !== 'number') return '—'
  const value = paise / 100
  const text = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  if (!signed) return value < 0 ? `(${text})` : text
  if (value === 0) return '0.00'
  return value > 0 ? `+${text}` : `−${text}`
}

export function TbRecon() {
  const client = useClient()
  const [rows, setRows] = useState<PeriodRecord[]>([])
  const [selected, setSelected] = useState<string | null>(null)
  const [body, setBody] = useState<TbReconBody | null>(null)
  const [tab, setTab] = useState<Tab>('income')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // Scoped to the client in the URL (ND-C1), and the selection is NOT
    // carried across a client change — see the note in Gstr3b.
    void periods
      .list({ client_id: client.id })
      .then((b) => {
        setRows(b.data)
        setSelected(b.data[0]?.id ?? null)
      })
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Returns could not be loaded.'),
      )
  }, [client.id])

  const run = useCallback(
    async (files: FileList | null) => {
      const file = files?.[0]
      if (!file || !selected) return
      setBusy(true)
      setError(null)
      try {
        setBody((await periods.tbReconciliation(selected, file)).data)
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'That reconciliation could not be run.')
        setBody(null)
      } finally {
        setBusy(false)
      }
    },
    [selected],
  )

  const r = body?.reconciliation
  const s = r?.summary

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="gst-h1">
            <Scale size={20} /> Books vs Return
          </h1>
          <p className="gst-sub">
            The trial balance against the prepared GSTR-1 — Income, GST and Exempt
          </p>
        </div>
        <div>
          <Button
            type="button"
            disabled={!selected || busy}
            onClick={() => fileInput.current?.click()}
            size="sm"
          >
            <Upload size={15} /> {busy ? 'Reading…' : 'Choose Main GST Data File'}
          </Button>
          <input
            ref={fileInput}
            type="file"
            accept=".xlsx,.xls"
            hidden
            onChange={(e) => {
              void run(e.target.files)
              e.target.value = ''
            }}
          />
        </div>
      </header>

      {error && <Alert tone="error" className="mb-4">{error}</Alert>}

      <div className="flex flex-wrap gap-6">
        <aside className="w-64 shrink-0">
          {rows.length === 0 ? (
            <div className="gst-empty">No periods yet.</div>
          ) : (
            <ul className="space-y-1">
              {rows.map((row) => (
                <li key={row.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelected(row.id)
                      setBody(null)
                    }}
                    aria-current={row.id === selected}
                    className="gst-rail-item"
                  >
                    <span className="truncate">{row.return_type}</span>
                    <span className="mt-0.5 block text-[11px]">{row.period}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        <section className="min-w-0 flex-1">
          {!r || !s ? (
            <div className="gst-empty">
              <p className="mb-2 font-medium">No trial balance read yet</p>
              <p>
                Choose the <strong>Main GST Data File</strong> — its <code>TB</code> sheet carries
                the month&rsquo;s income accounts and the GST treatment your firm assigned to each.
              </p>
            </div>
          ) : (
            <>
              {/* The legacy's stat strip. */}
              <div className="gst-stats mb-4">
                <div className="gst-stat">
                  <div className="n is-money">₹{rupees(s.tb_taxable)}</div>
                  <div className="l">TB TAXABLE</div>
                </div>
                <div className="gst-stat">
                  <div className="n is-money">₹{rupees(s.tb_exempt)}</div>
                  <div className="l">TB EXEMPT</div>
                </div>
                <div className="gst-stat">
                  <div className="n is-money">₹{rupees(s.gstr1_taxable)}</div>
                  <div className="l">GSTR-1 TAXABLE</div>
                </div>
                <div className="gst-stat">
                  <div className={`n is-money ${s.gap !== 0 ? 'is-tax' : ''}`}>
                    ₹{rupees(s.gap, true)}
                  </div>
                  <div className="l">TB − GSTR-1 GAP</div>
                </div>
              </div>

              <p className="gst-meta mb-3">
                {r.convention} · read from sheet <code>{body.sheet}</code>
              </p>

              {/* An account nobody classified is a question, shown as one. */}
              {r.unclassified.length > 0 && (
                <Alert tone="warning" className="mb-4">
                  <p className="gst-panel-h">
                    Unclassified income
                    <span className="gst-tag">
                      {r.unclassified.length} account{r.unclassified.length === 1 ? '' : 's'} ·
                      ₹{rupees(s.unclassified)}
                    </span>
                  </p>
                  <p className="mb-2 text-sm">
                    These carry an amount but no GST remark, so they are in no total above.
                    Classify them in the TB and read it again.
                  </p>
                  <ul className="gst-panel-list">
                    {r.unclassified.map((row, i) => (
                      <li key={i}>
                        <span className="gst-k is-soft">{String(row.account ?? '')}</span>
                        <span>
                          {String(row.description ?? '')} — ₹{rupees(row.amount)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              <div className="gst-tabs mb-3">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    className="gst-tab"
                    aria-selected={tab === t.key}
                    onClick={() => setTab(t.key)}
                  >
                    {t.label}
                    <span className="gst-count">
                      {t.key === 'income'
                        ? r.income.length
                        : t.key === 'gst'
                          ? r.gst.length
                          : r.exempt.length}
                    </span>
                  </button>
                ))}
              </div>

              {tab === 'income' && (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Account</th>
                        <th className="is-text">Description</th>
                        <th>TB Amount</th>
                        <th>Rate</th>
                        <th>GST Computed</th>
                        <th>GST Ledger</th>
                        <th>Diff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.income.map((row, i) => (
                        <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text is-sec">{String(row.account ?? '')}</td>
                          <td className="is-text">{String(row.description ?? '')}</td>
                          <td>{rupees(row.amount)}</td>
                          <td>{String(row.rate ?? '—')}</td>
                          <td>{rupees(row.gst_computed)}</td>
                          <td>{rupees(row.gst_ledger)}</td>
                          <td>{rupees(row.diff, true)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'gst' && (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Particulars</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.gst.map((row, i) => (
                        <tr key={row.particulars} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text is-sec">{row.particulars}</td>
                          <td>{rupees(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {tab === 'exempt' && (
                <div className="gst-table-wrap">
                  <table className="gst-table">
                    <thead>
                      <tr>
                        <th className="is-text">Account</th>
                        <th className="is-text">Description</th>
                        <th className="is-text">Treatment</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {r.exempt.map((row, i) => (
                        <tr key={i} className={i % 2 === 1 ? 'is-a' : ''}>
                          <td className="is-text is-sec">{String(row.account ?? '')}</td>
                          <td className="is-text">{String(row.description ?? '')}</td>
                          <td className="is-text">
                            <span className="gst-chip is-ok">{String(row.remark ?? '')}</span>
                          </td>
                          <td>{rupees(row.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  )
}
